import { useQuery } from "@tanstack/react-query";
import { useDirectoryUsers } from "@/hooks/useDirectoryUsers";
import { useConsoleUsers } from "@/hooks/rbac/useUsers";
import { supabase } from "@/integrations/supabase/client";
import { UnifiedUser, UserType, UnifiedUsersStats } from "@/types/unified-user";
import { useMemo } from "react";

export function useUnifiedUsers() {
  const { users: directoryUsers, isLoading: isLoadingDirectory } = useDirectoryUsers();
  const { data: consoleUsers, isLoading: isLoadingConsole } = useConsoleUsers();

  // Fetch all devices with enrollment_username
  const { data: devices, isLoading: isLoadingDevices } = useQuery({
    queryKey: ['devices-enrollment'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('devices')
        .select('id, name, status, last_seen, enrollment_username');
      
      if (error) throw error;
      return data || [];
    },
  });

  const isLoading = isLoadingDirectory || isLoadingConsole || isLoadingDevices;

  const { users, stats } = useMemo(() => {
    if (!devices || !directoryUsers || !consoleUsers) {
      return { 
        users: [], 
        stats: {
          total: 0,
          directory: 0,
          console: 0,
          both: 0,
          totalDevices: 0,
          activeUsers: 0,
        } as UnifiedUsersStats
      };
    }

    // Create username -> devices map (case-insensitive)
    const devicesMap = new Map<string, typeof devices>();
    devices.forEach(device => {
      if (device.enrollment_username) {
        const username = device.enrollment_username.toLowerCase();
        if (!devicesMap.has(username)) {
          devicesMap.set(username, []);
        }
        devicesMap.get(username)!.push(device);
      }
    });

    // Create email -> user map for console users
    const consoleUsersByEmail = new Map<string, typeof consoleUsers[0]>();
    consoleUsers.forEach(user => {
      consoleUsersByEmail.set(user.email.toLowerCase(), user);
    });

    // Create unified users list
    const unifiedUsers: UnifiedUser[] = [];
    const processedEmails = new Set<string>();

    // Process directory users first
    directoryUsers.forEach(dirUser => {
      const email = dirUser.email.toLowerCase();
      const username = dirUser.username.toLowerCase();
      const userDevices = devicesMap.get(username) || [];
      const consoleUser = consoleUsersByEmail.get(email);

      processedEmails.add(email);

      const type: UserType = consoleUser ? 'both' : 'directory';

      unifiedUsers.push({
        id: dirUser.id,
        type,
        email: dirUser.email,
        name: `${dirUser.firstName} ${dirUser.lastName}`,
        status: dirUser.status === 'active' ? 'active' : 'inactive',
        lastActive: dirUser.lastLogin || dirUser.syncedAt,
        
        // Directory fields
        directoryId: dirUser.id,
        username: dirUser.username,
        firstName: dirUser.firstName,
        lastName: dirUser.lastName,
        department: dirUser.department,
        title: dirUser.title,
        provider: dirUser.provider,
        customer: dirUser.customer,
        groups: dirUser.groups,
        externalId: dirUser.externalId,
        managerEmail: dirUser.managerEmail,
        attributes: dirUser.attributes,
        syncedAt: dirUser.syncedAt,
        
        // Console fields (if exists)
        consoleId: consoleUser?.id,
        roles: consoleUser?.user_roles,
        joinDate: consoleUser?.created_at,
        
        // Device relationship
        deviceCount: userDevices.length,
        devices: userDevices.map(d => ({
          id: d.id,
          name: d.name,
          status: d.status || 'unknown',
          lastSeen: d.last_seen,
        })),
      });
    });

    // Process console-only users
    consoleUsers.forEach(consoleUser => {
      const email = consoleUser.email.toLowerCase();
      
      if (!processedEmails.has(email)) {
        unifiedUsers.push({
          id: consoleUser.id,
          type: 'console',
          email: consoleUser.email,
          name: consoleUser.email.split('@')[0], // Extract name from email
          status: 'active', // Assume active if they have console access
          lastActive: consoleUser.last_sign_in_at || consoleUser.created_at,
          
          // Console fields
          consoleId: consoleUser.id,
          roles: consoleUser.user_roles,
          joinDate: consoleUser.created_at,
          
          // No devices for console-only users (no directory presence)
          deviceCount: 0,
          devices: [],
        });
      }
    });

    // Calculate stats
    const stats: UnifiedUsersStats = {
      total: unifiedUsers.length,
      directory: unifiedUsers.filter(u => u.type === 'directory' || u.type === 'both').length,
      console: unifiedUsers.filter(u => u.type === 'console' || u.type === 'both').length,
      both: unifiedUsers.filter(u => u.type === 'both').length,
      totalDevices: devices.length,
      activeUsers: unifiedUsers.filter(u => u.status === 'active').length,
    };

    return { users: unifiedUsers, stats };
  }, [devices, directoryUsers, consoleUsers]);

  return {
    users,
    stats,
    isLoading,
  };
}
